/**
 * tpl-free-ship-bar.js
 * Geo-aware shipping microcopy — one module, one market signal.
 *
 * Owns three surfaces:
 *   1. Cart-drawer free-shipping progress bar [data-tpl-free-ship-bar]
 *      (international only — India ships free at any order value)
 *   2. Shipping promise lines [data-tpl-ship-domestic] / [data-tpl-ship-intl]
 *      (PDP price block + cart drawer)
 *   3. INR expectation note [data-tpl-currency-note] — hidden only for
 *      CONFIRMED domestic visitors (a disclosure defaults to visible). Its
 *      [data-tpl-inr-paise] figure must STAY in rupees, so it is re-stamped
 *      after every Bucks conversion pass via a guarded reConvert wrapper.
 *
 * Market detection is tri-state: true = international, false = confirmed
 * domestic, null = no signal (promises fall back to domestic — India is the
 * primary market — but the disclosure note stays visible). Signals in order,
 * mirroring isInternationalNow() in tpl-frame-picker.js:
 *   1. Bucks converter localStorage (set early, persists across visits)
 *   2. Shopify Markets currency/country — only a NON-India value is a signal;
 *      'INR'/'IN' is every visitor's default (single presentment currency,
 *      no market auto-redirect) and proves nothing
 *   3. Rendered money symbol after Bucks converts the DOM
 *
 * Threshold: ₹10,494 INR — the free-international-shipping zone rule (≈ €99).
 * Amounts injected into hints use <span class="money"> so Bucks re-renders
 * them in the visitor's display currency.
 *
 * Triggers: DOMContentLoaded, cart:item-added, cart:updated, Bucks init poll.
 */
(function () {
  'use strict';

  var THRESHOLD_INR_PAISE = 1049400; // ₹10,494 = free intl shipping threshold

  function intlSignal() {
    try {
      var keys = ['buckscc_currency', 'buckscc_selected_currency', 'buckscc_auto_currency'];
      for (var i = 0; i < keys.length; i++) {
        var raw = window.localStorage && localStorage.getItem(keys[i]);
        if (!raw) continue;
        var cur = null;
        try {
          var v = JSON.parse(raw);
          cur = (v && typeof v === 'object' && (v.currency || v.code)) || (typeof v === 'string' ? v : null);
        } catch (_) {
          cur = raw;
        }
        if (cur) return String(cur).toUpperCase().indexOf('INR') === -1;
      }
    } catch (_) {}
    try {
      if (window.Shopify && Shopify.currency && Shopify.currency.active && Shopify.currency.active !== 'INR') return true;
      if (window.Shopify && Shopify.country && String(Shopify.country).toUpperCase() !== 'IN') return true;
    } catch (_) {}
    try {
      var m = document.querySelector('.price .money, .money');
      if (m && m.textContent && /[$€£¥₩]/.test(m.textContent) && !/₹/.test(m.textContent)) return true;
    } catch (_) {}
    return null;
  }

  function formatRupees(paise) {
    /* Western grouping ('en'), not lakh ('en-IN') — must match the Shopify
       money format used by the adjacent subtotal (₹104,940, not ₹1,04,940). */
    return '₹' + Math.round(paise / 100).toLocaleString('en');
  }

  function setInrFigures() {
    var els = document.querySelectorAll('[data-tpl-inr-paise]');
    for (var i = 0; i < els.length; i++) {
      var paise = parseInt(els[i].getAttribute('data-tpl-inr-paise'), 10);
      if (!isFinite(paise)) continue;
      var want = formatRupees(paise);
      if (els[i].textContent !== want) els[i].textContent = want;
    }
  }

  function guardBucks() {
    try {
      if (!window.bucksCC || typeof bucksCC.reConvert !== 'function' || bucksCC.__tplInrGuard) return;
      var orig = bucksCC.reConvert;
      bucksCC.reConvert = function () {
        var out = orig.apply(this, arguments);
        setInrFigures();
        /* Bucks conversion passes usually mean fresh geo state (its async IP
           lookup lands AFTER the global appears) — re-run the toggle so the
           PDP promise corrects for first-visit international sessions.
           Safe: update() is convergent, and it only calls reConvert when a
           gap value or span visibility actually changed. */
        deferredUpdate();
        return out;
      };
      bucksCC.__tplInrGuard = true;
    } catch (_) {}
  }

  function bucksReconvert() {
    try {
      if (window.bucksCC && typeof bucksCC.reConvert === 'function') bucksCC.reConvert();
    } catch (_) {}
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .catch(function () {
        return null;
      });
  }

  /* Every write below is read-before-write: if the DOM already holds the
     wanted state, nothing is touched. That makes update() convergent — the
     MutationObserver pass it triggers finds nothing to change and stops —
     so observing the drawer can never loop. */

  function setHidden(el, want) {
    if (el.hidden !== want) el.hidden = want;
  }

  function render(bar, cart) {
    if (!cart || cart.item_count === 0) {
      setHidden(bar, true);
      return;
    }
    setHidden(bar, false);
    var pct = Math.min(100, Math.round((cart.total_price / THRESHOLD_INR_PAISE) * 100));
    var unlocked = pct >= 100;
    /* 2026-09-06 (E10): the INR expectation note says the checkout total is
       "+ shipping". Once the cart clears the threshold there is no shipping to
       add, so hide that clause. Read-before-write via setHidden keeps it
       convergent — this runs on every cart update. */
    var plus = document.querySelectorAll('[data-tpl-plus-shipping]');
    for (var p = 0; p < plus.length; p++) setHidden(plus[p], unlocked);
    var fill = bar.querySelector('[data-fs-fill]');
    var label = bar.querySelector('[data-fs-label]');
    var hint = bar.querySelector('[data-fs-hint]');
    if (fill && fill.style.width !== pct + '%') fill.style.width = pct + '%';
    var wantLabel = unlocked ? 'UNLOCKED' : pct + '%';
    if (label && label.textContent !== wantLabel) label.textContent = wantLabel;
    if (hint) {
      if (unlocked) {
        if (hint.textContent !== 'Free worldwide shipping · included') {
          hint.textContent = 'Free worldwide shipping · included';
        }
        /* Clear the gap marker so dropping back below the SAME total later
           still rewrites the hint (stale-attr bug, review finding). */
        hint.removeAttribute('data-fs-gap');
      } else {
        var gap = THRESHOLD_INR_PAISE - cart.total_price;
        /* Bucks rewrites the .money span after we set it, so innerHTML can
           never equal what we wrote — track the gap in an attribute instead
           (attribute writes don't wake the childList observer). */
        if (hint.getAttribute('data-fs-gap') !== String(gap)) {
          hint.setAttribute('data-fs-gap', String(gap));
          hint.innerHTML = '<span class="money">' + formatRupees(gap) + '</span> away from free worldwide shipping';
          bucksReconvert();
        }
      }
    }
    bar.classList.toggle('tpl-free-ship--unlocked', unlocked);
  }

  var fetchSeq = 0;

  function update() {
    guardBucks();
    var s = intlSignal();
    var intl = s === true;
    var els, i;
    var revealedIntl = false;
    els = document.querySelectorAll('[data-tpl-ship-domestic]');
    for (i = 0; i < els.length; i++) setHidden(els[i], intl);
    els = document.querySelectorAll('[data-tpl-ship-intl]');
    for (i = 0; i < els.length; i++) {
      if (intl && els[i].hidden) revealedIntl = true;
      setHidden(els[i], !intl);
    }
    els = document.querySelectorAll('[data-tpl-currency-note]');
    for (i = 0; i < els.length; i++) setHidden(els[i], s === false);
    setInrFigures();
    /* A span Bucks has never seen (it was display:none during its pass) may
       still hold raw ₹ — ask for a conversion pass on reveal. */
    if (revealedIntl) bucksReconvert();

    var bars = document.querySelectorAll('[data-tpl-free-ship-bar]');
    if (!intl) {
      for (i = 0; i < bars.length; i++) setHidden(bars[i], true);
      fetchSeq++; /* invalidate any in-flight intl fetch so it can't re-show the bar */
      return;
    }
    var seq = ++fetchSeq;
    fetchCart().then(function (cart) {
      if (seq !== fetchSeq) return; /* superseded by a newer update — drop stale cart state */
      for (var j = 0; j < bars.length; j++) render(bars[j], cart);
      setInrFigures();
    });
  }

  function deferredUpdate() {
    setTimeout(update, 60);
  }

  /* The drawer swaps its innerHTML via the Section Rendering API after cart
     mutations (and other modules patch it too), which resets every toggle to
     the server-rendered default. Events fire BEFORE the swap lands, so a
     fixed delay always loses the race eventually — watch the subtree instead
     and re-apply after any structural change. update() is convergent (see
     above), so this cannot ping-pong. */
  var observeTimer = null;
  function watchDrawer() {
    if (!window.MutationObserver) return;
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    new MutationObserver(function () {
      if (observeTimer) clearTimeout(observeTimer);
      observeTimer = setTimeout(update, 100);
    }).observe(drawer, { childList: true, subtree: true });
  }

  function init() {
    update();
    watchDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('cart:item-added', deferredUpdate);
  document.addEventListener('cart:updated', deferredUpdate);
  /* Dawn's /cart page re-renders .js-contents and fires ONLY cart:count-updated
     (the page's bar sits outside the drawer, so the MutationObserver can't see
     it) — without this, quantity changes on /cart leave the bar stale. */
  document.addEventListener('cart:count-updated', deferredUpdate);
  /* bfcache restore (iOS Safari back-button): the document resumes as-left,
     but the cart may have changed on the interim page with no events here. */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) deferredUpdate();
  });

  /* Bucks initialises on its own schedule. Poll briefly: once it appears,
     wrap reConvert and re-run the toggle (its localStorage geo signal may
     have just become readable). */
  var tries = 0;
  var timer = setInterval(function () {
    tries += 1;
    if (window.bucksCC) {
      clearInterval(timer);
      update();
    } else if (tries > 20) {
      clearInterval(timer);
    }
  }, 500);
  setTimeout(setInrFigures, 2500);
})();
